// ─────────────────────────────────────────────────────────────
// AgentSearchDropdown
//
// A searchable agent picker for admin filter rows. Agent lists are small
// enough to fetch in full up front, so this filters client-side as you
// type rather than hitting a search endpoint (contrast UserSearchDropdown,
// which paginates against a server search since the user list is large).
//
// Usage:
//   <AgentSearchDropdown agents={agents} value={agentId} onChange={setAgentId} />
// ─────────────────────────────────────────────────────────────
import { useState, useRef, useEffect, useMemo } from 'react'

export interface AgentSearchOption {
  id: string
  name: string
}

interface AgentSearchDropdownProps {
  agents: AgentSearchOption[]
  value: string
  onChange: (agentId: string) => void
  placeholder?: string
  minWidth?: string | number
  icon?: string
  searchPlaceholder?: string
}

export function AgentSearchDropdown({
  agents,
  value,
  onChange,
  placeholder = 'All agents',
  minWidth = '180px',
  icon = '🤖',
  searchPlaceholder = 'Search agents…',
}: AgentSearchDropdownProps) {
  const [open, setOpen]   = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef           = useRef<HTMLDivElement>(null)

  const selected = agents.find(a => a.id === value) ?? null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return agents
    return agents.filter(a => a.name.toLowerCase().includes(q))
  }, [agents, query])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleOpen = () => {
    setOpen(o => !o)
    if (!open) setQuery('')
  }

  const handleSelect = (agentId: string) => {
    onChange(agentId)
    setOpen(false)
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth }}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.3rem 0.65rem', borderRadius: 6, fontSize: '0.75rem',
          background: value ? 'rgba(124,58,237,0.06)' : 'var(--bg-elevated)',
          border: `1px solid ${value ? 'var(--accent-primary)' : 'var(--border)'}`,
          color: value ? 'var(--text-primary)' : 'var(--text-muted)',
          cursor: 'pointer', minWidth: '160px',
        }}
      >
        {selected ? (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
            {icon} {selected.name}
          </span>
        ) : (
          <span>{placeholder}</span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.65rem' }}>▾</span>
        {value && (
          <span
            onClick={e => { e.stopPropagation(); onChange('') }}
            style={{ marginLeft: '0.25rem', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1 }}
          >
            ✕
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
          width: '240px', background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', overflow: 'hidden',
        }}>
          <div style={{ padding: '0.4rem' }}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                width: '100%', padding: '0.35rem 0.6rem', borderRadius: 6, fontSize: '0.75rem',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {/* "All agents" option */}
            <div
              onClick={() => handleSelect('')}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.78rem',
                color: !value ? 'var(--accent-primary)' : 'var(--text-secondary)',
                background: !value ? 'rgba(124,58,237,0.08)' : 'transparent',
              }}
            >
              <span style={{ fontSize: '1rem' }}>{icon}</span> {placeholder}
            </div>

            {filtered.map(a => (
              <div
                key={a.id}
                onClick={() => handleSelect(a.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.78rem',
                  color: value === a.id ? 'var(--accent-primary)' : 'var(--text-primary)',
                  background: value === a.id ? 'rgba(124,58,237,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { if (value !== a.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)' }}
                onMouseLeave={e => { if (value !== a.id) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              >
                <span>{icon}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
              </div>
            ))}

            {filtered.length === 0 && (
              <div style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
